if (!customElements.get('warmoura-header')) {
  customElements.define(
    'warmoura-header',
    class WarmouraHeader extends HTMLElement {
      connectedCallback() {
        this.handleScroll = this.handleScroll.bind(this);
        this.handleScroll();
        window.addEventListener('scroll', this.handleScroll, { passive: true });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.handleScroll);
      }

      handleScroll() {
        // A locked body is pinned by taking it out of flow, which reads as
        // scrollY 0 even though the page has not moved. Without this the header
        // would expand the moment the cart opened and snap back on close.
        if (document.body.classList.contains('overflow-hidden')) return;
        this.classList.toggle('is-scrolled', window.scrollY > 8);
      }
    }
  );
}

if (!customElements.get('warmoura-faq')) {
  customElements.define(
    'warmoura-faq',
    class WarmouraFaq extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('details'));
        this.handleToggle = this.handleToggle.bind(this);

        this.items.forEach((item) => {
          item.addEventListener('toggle', this.handleToggle);
          this.syncItem(item);
        });
      }

      disconnectedCallback() {
        this.items?.forEach((item) => item.removeEventListener('toggle', this.handleToggle));
      }

      handleToggle(event) {
        const activeItem = event.currentTarget;

        if (activeItem.open) {
          this.items.forEach((item) => {
            if (item !== activeItem && item.open) item.open = false;
            this.syncItem(item);
          });
        }

        this.syncItem(activeItem);
      }

      syncItem(item) {
        item.querySelector('summary')?.setAttribute('aria-expanded', item.open ? 'true' : 'false');
      }
    }
  );
}

if (!customElements.get('warmoura-category-toggle')) {
  customElements.define(
    'warmoura-category-toggle',
    class WarmouraCategoryToggle extends HTMLElement {
      connectedCallback() {
        this.indicator = this.querySelector('[data-toggle-indicator]');
        this.options = Array.from(this.querySelectorAll('[data-toggle-option]'));
        if (!this.indicator || this.options.length === 0) return;

        // Only the visual pill is scripted; the options stay plain links so
        // navigation works without JavaScript.
        this.classList.add('is-hydrated');
        this.move = this.move.bind(this);
        this.move();

        if (typeof ResizeObserver === 'function') {
          this.observer = new ResizeObserver(this.move);
          this.observer.observe(this);
        }
        window.addEventListener('resize', this.move, { passive: true });

        // Slide toward the option the pointer or keyboard is on, then settle
        // back on the current page's option.
        this.options.forEach((option) => {
          option.addEventListener('mouseenter', () => this.move(option));
          option.addEventListener('focus', () => this.move(option));
          option.addEventListener('mouseleave', () => this.move());
          option.addEventListener('blur', () => this.move());
        });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        window.removeEventListener('resize', this.move);
      }

      move(target) {
        const active =
          (target instanceof HTMLElement && target) ||
          this.options.find((option) => option.classList.contains('is-selected')) ||
          this.options[0];
        if (!active) return;
        const trackLeft = this.indicator.parentElement.getBoundingClientRect().left;
        const rect = active.getBoundingClientRect();
        this.indicator.style.width = `${rect.width}px`;
        this.indicator.style.transform = `translateX(${rect.left - trackLeft}px)`;
        this.indicator.classList.add('is-ready');
      }
    }
  );
}

/*
 * Footer navigation groups.
 *
 * One markup for both layouts: real buttons with aria-expanded and
 * aria-controls. Below the mobile breakpoint they collapse their panel; at or
 * above it every panel is open and the button stops being interactive, so the
 * columns read as plain headings.
 */
if (!customElements.get('warmoura-footer')) {
  customElements.define(
    'warmoura-footer',
    class WarmouraFooter extends HTMLElement {
      connectedCallback() {
        this.groups = Array.from(this.querySelectorAll('[data-footer-group]')).map((group) => ({
          group,
          toggle: group.querySelector('[data-footer-toggle]'),
          panel: group.querySelector('[data-footer-panel]'),
        }));
        this.single = this.dataset.accordionSingle !== 'false';
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.handleToggle = this.handleToggle.bind(this);
        this.applyLayout = this.applyLayout.bind(this);

        this.groups.forEach(({ toggle }) => toggle?.addEventListener('click', this.handleToggle));
        // Safari before 14 only has the deprecated listener API.
        if (this.mobile.addEventListener) this.mobile.addEventListener('change', this.applyLayout);
        else this.mobile.addListener(this.applyLayout);

        this.bindForm();
        this.watchForFloatingPill();
        // Tells the stylesheet that collapsing is now safe.
        this.classList.add('is-enhanced');
        this.applyLayout();
      }

      /* On a phone the market picker is a floating pill by the chat button. Once
         the footer arrives it carries its own market row, and two controls for
         the same choice on one screen is a question the customer should not have
         to answer. The pill stands down while the footer is in view.

         Purely additive: if IntersectionObserver is missing the flag is never
         set, the pill simply stays, and nothing else depends on it. */
      watchForFloatingPill() {
        if (!('IntersectionObserver' in window)) return;

        this.pillObserver = new IntersectionObserver(
          ([entry]) => {
            document.documentElement.classList.toggle('wm-footer-in-view', entry.isIntersecting);
          },
          { rootMargin: '0px 0px -25% 0px' }
        );
        this.pillObserver.observe(this);
      }

      disconnectedCallback() {
        this.classList.remove('is-enhanced');
        this.groups?.forEach(({ toggle }) => toggle?.removeEventListener('click', this.handleToggle));
        if (this.mobile?.removeEventListener) this.mobile.removeEventListener('change', this.applyLayout);
        else this.mobile?.removeListener(this.applyLayout);
        this.form?.removeEventListener('submit', this.handleSubmit);
        this.pillObserver?.disconnect();
        document.documentElement.classList.remove('wm-footer-in-view');
      }

      applyLayout() {
        const collapsed = this.mobile.matches;
        this.groups.forEach(({ group, toggle }, index) => {
          if (!toggle) return;
          // Desktop keeps everything open; mobile starts closed, except the
          // first group when only one may be open, so the panel is never a
          // blank wall of headings.
          const expanded = collapsed ? (this.single ? index === 0 : false) : true;
          this.setExpanded(group, toggle, expanded);
          toggle.disabled = !collapsed;
        });
      }

      handleToggle(event) {
        const toggle = event.currentTarget;
        const open = toggle.getAttribute('aria-expanded') === 'true';
        if (this.single && !open) {
          this.groups.forEach((entry) => {
            if (entry.toggle) this.setExpanded(entry.group, entry.toggle, false);
          });
        }
        const entry = this.groups.find((item) => item.toggle === toggle);
        this.setExpanded(entry?.group, toggle, !open);
      }

      // The class carries the open state for CSS: the panel is a sibling of the
      // heading that wraps the button, not of the button itself, so a sibling
      // selector on aria-expanded would never match.
      setExpanded(group, toggle, expanded) {
        toggle.setAttribute('aria-expanded', String(expanded));
        group?.classList.toggle('is-open', expanded);
      }

      // The newsletter posts as a normal Shopify form; this only marks the
      // button busy so a slow connection does not look unresponsive.
      bindForm() {
        this.form = this.querySelector('.warmoura-footer__form');
        if (!this.form) return;
        this.handleSubmit = () => {
          if (!this.form.checkValidity()) return;
          this.form.classList.add('is-loading');
        };
        this.form.addEventListener('submit', this.handleSubmit);
      }
    }
  );
}

/*
 * Range hero pagination.
 *
 * The hero itself is CSS: a native scroll-snap track on mobile, flex expansion
 * on desktop. This only keeps the dots honest about which card is in view, so
 * it does nothing at all when there are no dots to paint.
 */
if (!customElements.get('warmoura-range-hero-dots')) {
  const paintRangeHeroDots = (hero) => {
    const track = hero.querySelector('.warmoura-rangehero__track');
    const dots = Array.from(hero.querySelectorAll('.warmoura-rangehero__dot'));
    const panels = Array.from(hero.querySelectorAll('.warmoura-rangehero__panel'));
    if (!track || dots.length === 0 || dots.length !== panels.length) return;
    if (!('IntersectionObserver' in window)) return;

    const ratios = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio));
        let best = 0;
        let bestRatio = -1;
        panels.forEach((panel, index) => {
          const ratio = ratios.get(panel) ?? 0;
          if (ratio > bestRatio + 0.01) {
            bestRatio = ratio;
            best = index;
          }
        });
        dots.forEach((dot, index) => dot.classList.toggle('is-active', index === best));
      },
      { root: track, threshold: [0, 0.25, 0.5, 0.75, 0.99] }
    );
    panels.forEach((panel) => observer.observe(panel));
    hero.dataset.dotsBound = 'true';
  };

  const initRangeHeroes = (scope) => {
    (scope || document).querySelectorAll('.warmoura-rangehero').forEach((hero) => {
      if (hero.dataset.dotsBound === 'true') return;
      paintRangeHeroDots(hero);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initRangeHeroes());
  if (document.readyState !== 'loading') initRangeHeroes();
  // Theme Editor section reloads hand back a fresh subtree.
  document.addEventListener('shopify:section:load', (event) => initRangeHeroes(event.target));
}

/**
 * Country and language selector.
 *
 * The forms are Shopify's own; this only opens and closes the list. Choosing an
 * option submits the form, so the market, currency and locale are set by the
 * platform rather than by anything stored here. No automatic redirect is ever
 * performed: a customer's country is detected by Shopify for pricing, and the
 * selector is there to override it by hand.
 */
(() => {
  if (window.WarmouraLocaleBound) return;
  window.WarmouraLocaleBound = true;

  // One open panel at a time, one way in and one way out.
  //
  // The panel animates, so `hidden` cannot be toggled directly: it is cleared
  // first and the open class added on the next frame so there is a state to
  // transition from, and restored only once the exit has played. CLOSE_MS
  // matches the exit transition in warmoura.css.
  const CLOSE_MS = 160;

  // Everything about the current open cycle lives here, so closing is a single
  // idempotent teardown rather than a list of things to remember.
  let session = null;

  const releaseScrollLock = () => {
    document.documentElement.classList.remove('warmoura-locale-open');
    document.body.classList.remove('warmoura-locale-open');
  };

  const closeMarketPanel = () => {
    // Idempotent: calling it with nothing open is a no-op, and calling it twice
    // in a row cannot leave half a teardown behind.
    releaseScrollLock();
    if (!session) return;

    const { list, toggle, controller, timer } = session;
    session = null;

    // Listeners belonging to this cycle die with it.
    controller.abort();
    window.clearTimeout(timer);

    toggle?.setAttribute('aria-expanded', 'false');
    list.classList.remove('is-open');
    // `hidden` returns after the exit so the panel is not yanked off screen.
    // Held on the element, not in `session`, so a re-open can cancel it.
    list.__wmHideTimer = window.setTimeout(() => {
      list.hidden = true;
    }, CLOSE_MS);
  };

  const openMarketPanel = (list, toggle) => {
    // Never stack cycles: whatever was open is fully torn down first.
    closeMarketPanel();
    window.clearTimeout(list.__wmHideTimer);

    const controller = new AbortController();
    const { signal } = controller;

    list.hidden = false;
    toggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('warmoura-locale-open');
    document.body.classList.add('warmoura-locale-open');

    // Two frames: one for `hidden` to be gone, one for the closed state to be
    // committed before the class that transitions away from it.
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => list.classList.add('is-open'));
    });

    // A panel anchored to a corner has no business surviving a rotation or a
    // keyboard opening, so this cycle ends with either.
    window.addEventListener('resize', closeMarketPanel, { signal, passive: true });
    window.addEventListener('orientationchange', closeMarketPanel, { signal, passive: true });
    // Any other overlay taking the screen closes this one too.
    document.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('#cart-icon-bubble, cart-drawer, shopify-chat')) closeMarketPanel();
      },
      { signal, capture: true }
    );

    session = { list, toggle, controller, timer: 0, frame };
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-locale-toggle]');
    if (toggle) {
      const list = toggle.parentElement.querySelector('[data-locale-list]');
      if (!list) return;
      if (session && session.list === list) closeMarketPanel();
      else openMarketPanel(list, toggle);
      return;
    }
    // A tap anywhere that is not the control or the panel dismisses it.
    if (!event.target.closest('[data-warmoura-locale]')) closeMarketPanel();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeMarketPanel();
  });

  // Choosing a market submits the form and navigates. The teardown runs anyway,
  // so an intercepted or cancelled submit can never strand the page locked.
  document.addEventListener('submit', (event) => {
    if (event.target.closest('[data-warmoura-locale]')) closeMarketPanel();
  });

  // Back/forward cache restores and section re-renders both land here.
  window.addEventListener('pagehide', closeMarketPanel);
  window.addEventListener('pageshow', releaseScrollLock);
  document.addEventListener('shopify:section:load', closeMarketPanel);
})();

/*
 * The floating country pill is entirely CSS.
 *
 * It used to measure the Shopify Inbox activator through its shadow root and
 * publish the offset as a custom property, then be lifted again whenever the
 * sticky purchase bar appeared. Between the two, the pill moved while the page
 * scrolled. It now rests on one permanent offset that matches where Inbox
 * parks its own button, so there is nothing left to measure, no resize or
 * orientation listener, and no observer watching for the widget.
 */
